import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface CptRankingResult {
  primary_cpt: string;
  ordered_cpts: string[];
  role_map: Record<string, 'primary' | 'secondary'>;
  reasons: string[];
  warnings: string[];
  confidence: 'high' | 'medium' | 'low';
  ranking_source: 'payer_override' | 'pair_rule' | 'category_weight' | 'historical_boost' | 'heuristic' | 'single';
  matched_pair_rule?: { cpt_a: string; cpt_b: string; reason: string };
}

interface UseCptRankingArgs {
  cptCodes: string[];
  payerPlanId?: string | null;
  surgeonId?: string;
  facilityId?: string | null;
  specialtyDomainId?: string | null;
  enabled?: boolean;
}

const LOCAL_ORTHOPEDIC_CPT_WEIGHTS: Record<string, number> = {
  // Knee
  '27447': 100,
  '27570': 90,
  '27385': 88,
  '29880': 84,
  '29881': 84,
  // Shoulder
  '29807': 92,
  '23420': 88,
  '29827': 88,
  '23430': 78,
  '29828': 78,
  '23120': 72,
  '29824': 72,
  '29826': 60,
  '29823': 55,
  '29822': 50,
  // Hand
  '64721': 86,
};

function buildLocalOrthopedicRanking(cptCodes: string[]): CptRankingResult | null {
  const rankable = cptCodes.filter(code => LOCAL_ORTHOPEDIC_CPT_WEIGHTS[code] !== undefined);
  if (rankable.length === 0) return null;

  const ordered = [...cptCodes].sort((a, b) =>
    (LOCAL_ORTHOPEDIC_CPT_WEIGHTS[b] ?? 0) - (LOCAL_ORTHOPEDIC_CPT_WEIGHTS[a] ?? 0),
  );
  const primary = ordered[0];

  return {
    primary_cpt: primary,
    ordered_cpts: ordered,
    role_map: Object.fromEntries(ordered.map(code => [code, code === primary ? 'primary' as const : 'secondary' as const])),
    reasons: ['Local orthopedic CPT catalog ranked this demo bundle.'],
    warnings: [],
    confidence: rankable.length === cptCodes.length ? 'high' : 'medium',
    ranking_source: 'category_weight',
  };
}

export function useCptRanking({
  cptCodes,
  payerPlanId,
  surgeonId,
  facilityId,
  specialtyDomainId,
  enabled = true,
}: UseCptRankingArgs) {
  const [ranking, setRanking] = useState<CptRankingResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRanking = useCallback(async () => {
    if (!enabled || cptCodes.length === 0) {
      setRanking(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('rank-primary', {
        body: {
          cpt_codes: cptCodes,
          payer_plan_id: payerPlanId || undefined,
          surgeon_id: surgeonId || '',
          facility_id: facilityId || '',
          specialty_domain_id: specialtyDomainId || undefined,
        },
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      const remoteRanking = data as CptRankingResult;
      const localRanking = buildLocalOrthopedicRanking(cptCodes);
      const remoteMissingLocalCodes = Boolean(localRanking) && (remoteRanking.warnings || []).some(warning =>
        /not found in catalog|equal rank weight|default weight/i.test(warning),
      );

      setRanking(remoteMissingLocalCodes ? localRanking : remoteRanking);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to rank CPTs';
      setError(message);
      // Fallback: first code is primary
      const fallback: CptRankingResult = {
        primary_cpt: cptCodes[0],
        ordered_cpts: cptCodes,
        role_map: Object.fromEntries(cptCodes.map((c, i) => [c, i === 0 ? 'primary' as const : 'secondary' as const])),
        reasons: ['Fallback: using original order'],
        warnings: [`Ranking engine unavailable: ${message}`],
        confidence: 'low',
        ranking_source: 'heuristic',
      };
      setRanking(fallback);
    } finally {
      setIsLoading(false);
    }
  }, [cptCodes.join(','), payerPlanId, surgeonId, facilityId, specialtyDomainId, enabled]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  return { ranking, isLoading, error, refetch: fetchRanking };
}
